import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDocumentTitle } from '@/features/common/useDocumentTitle';
import { roleLabel } from '@/i18n/roleLabels';
import { auth } from '@/i18n/dictionaries/auth';
import { useTranslation } from '@/i18n/useTranslation';
import '@/features/auth/auth.css';
import { mockAuthApi } from '@/features/auth/api/mockAuthApi';
import type { GrantableRole } from '@/features/auth/types/GrantableRole';

/**
 * The dev/E2E stand-in for the MPass sign-in screen, served at /saml/authenticate/{registrationId}
 * under the `mock-gov` profile only (MockAuthController).
 *
 * Replaces `auth/mpass-mock.html`. Deliberately chrome-less: no gov-bar, no site nav, full-viewport
 * centred card. The page represents "you are on the MPass website now", the way a real IdP redirect
 * would look, and carrying AKIS's own header would undercut that — MpassMockLoginContractTest
 * guarded the absence of the gov-bar for exactly this reason, and mpass-auth.spec.ts guards it now
 * that the markup is built in the browser.
 *
 * It submits a native form POST to /mock/login-form rather than fetching, on purpose. That endpoint
 * establishes the session server-side and answers with a redirect — to the cabinet, or to
 * /auth/mpass-error for a blocked account — and a plain form lets the browser follow it exactly as
 * before. Nothing about the sign-in contract moved into the client; only the markup did. (The E2E
 * suites do not come through here at all: their mockLogin helpers POST straight to /mock/login.)
 */
export function MpassMockPage() {
  const { t, language } = useTranslation(auth);
  const [params] = useSearchParams();
  const [roles, setRoles] = useState<GrantableRole[]>([]);

  useDocumentTitle(t('mockTitle'));

  // On a rejected IDNP the server bounces back here with what was typed, so the field values are
  // initial state seeded from the query string rather than constants.
  const [name, setName] = useState(params.get('name') ?? 'Ion Fermier');
  const [idnp, setIdnp] = useState(params.get('idnp') ?? '2000000000001');
  const [role, setRole] = useState(params.get('role') ?? '');
  const [replaceRoles, setReplaceRoles] = useState(
    // Checked by default: grantRole is additive, so without this a reused IDNP accumulates roles
    // across logins and per-role testing becomes impossible.
    params.get('replaceRoles') !== 'false',
  );

  const [rolesFailed, setRolesFailed] = useState(false);

  useEffect(() => {
    let live = true;
    mockAuthApi
      .roles()
      .then((r) => {
        if (live) setRoles(r);
      })
      // Without this the rejection is unhandled and the dropdown just stays empty, which reads as
      // "this build has no roles" rather than "the request failed". The form still works — the
      // role is optional — so this reports the gap instead of blocking sign-in.
      .catch(() => {
        if (live) setRolesFailed(true);
      });
    return () => {
      live = false;
    };
  }, []);

  // roleLabel answers with the code itself for anything its dictionary does not carry. When that
  // happens the controller's own Romanian label is a better thing to show than a raw enum name.
  const labelFor = (r: GrantableRole): string => {
    const known = roleLabel(r.code, language);
    return known === r.code ? r.label : known;
  };

  return (
    // The outer box is what centres the card in the viewport. The template did that by styling
    // `body`, which inside an SPA would leak onto every other route.
    <div className="mpass-mock">
      <div className="mpass-mock__wrap">
        <div className="mpass-mock__card card">
          <h1>
            <img src="/img/gov/mpass.svg" alt="" />
            {t('mockHeading')}
          </h1>
          <p>{t('mockSubtitle')}</p>
          <div className="mpass-mock__note">
            {t('mockNoteBefore')} <code>mock-gov</code> {t('mockNoteAfter')}
          </div>

          {params.get('error') === 'idnp' && (
            <p className="mpass-mock__error" role="alert">
              {t('mockErrorIdnp')}
            </p>
          )}

          {/* autoComplete="off": Chrome otherwise restores prior form state on back/soft reload,
            silently un-checking "Doar rolul selectat" and turning what looks like a clean
            per-role login back into an additive one. */}
          <form method="post" action="/mock/login-form" autoComplete="off">
            <label className="mpass-mock__field">
              {t('mockName')}{' '}
              <span className="mpass-mock__required" aria-hidden="true">
                *
              </span>
              <input name="name" value={name} onChange={(e) => setName(e.target.value)} required />
            </label>

            <label className="mpass-mock__field">
              {t('mockIdnp')}{' '}
              <span className="mpass-mock__required" aria-hidden="true">
                *
              </span>
              <span className="mpass-mock__hint">{t('mockIdnpHint')}</span>
              <input
                name="idnp"
                value={idnp}
                onChange={(e) => setIdnp(e.target.value)}
                required
                pattern="[0-9]{13}"
                inputMode="numeric"
              />
            </label>

            <label className="mpass-mock__field">
              {t('mockRole')}
              {rolesFailed && <span className="mpass-mock__hint">{t('mockRolesUnavailable')}</span>}
              <select name="role" value={role} onChange={(e) => setRole(e.target.value)}>
                <option value="">{t('mockRoleNone')}</option>
                {roles.map((r) => (
                  // The frontend's own role dictionary is bilingual, so it wins where it knows the
                  // code; the controller's Romanian label is the fallback for one it does not.
                  <option key={r.code} value={r.code}>
                    {labelFor(r)}
                  </option>
                ))}
              </select>
            </label>

            <label className="mpass-mock__field mpass-mock__checkbox">
              <input
                type="checkbox"
                name="replaceRoles"
                value="true"
                checked={replaceRoles}
                onChange={(e) => setReplaceRoles(e.target.checked)}
              />
              {t('mockReplaceRoles')}
            </label>

            {/* Same button markup as the public header's own MPass CTA — one icon treatment
              site-wide instead of a second, bespoke one just for this page. */}
            <button type="submit" className="btn btn--primary btn--md mpass-mock__submit">
              <img className="btn__logo" src="/img/gov/mpass.svg" alt="" aria-hidden="true" />
              <span>{t('mockSubmit')}</span>
            </button>
          </form>
        </div>
        <p className="mpass-mock__disclaimer">{t('mockDisclaimer')}</p>
      </div>
    </div>
  );
}
